import {Component, OnDestroy, OnInit} from "@angular/core";
import {Vehicle} from "../../../models";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {AppService, DealService, VehicleService} from "../../../services";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import * as dealActions from "../../../store/actions/deal.actions";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../store/state";
import {VehicleUnavailableError, VehicleWithCondition} from "../../../models/vehicle";
import swal, {SweetAlertOptions} from "sweetalert2";
import {SettingsService} from "../../../../settings-module/services";

@Component({
  selector: "app-vehicle-lookup",
  templateUrl: "./vehicle-lookup.component.html",
  styleUrls: ["./vehicle-lookup.component.scss"]
})
export class VehicleLookupComponent implements OnInit, OnDestroy {

  vehicles: VehicleWithCondition[] = [];
  filteredVehicles: VehicleWithCondition[] = [];
  searchText: string;
  sortParam = "year";
  filter = false;
  ascending = false;
  changeDealOnSelectVehicle = true;
  dispatchStockNumberOnly = false;
  public getSingleVehiclePanelOpen: boolean = false;
  public getAllVehiclesPanelOpen: boolean = false;
  private unsubscribe$ = new Subject();

  getInventoryVin: string = "";

  updateGetInventoryVin($event) {
    this.getInventoryVin = $event;
    // console.log("updateGetInventoryVin:", this.getInventoryVin)
  }

  constructor(
    private appService: AppService,
    private dealService: DealService,
    private vehicleService: VehicleService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<fromRoot.DealState>,
    private settingsService: SettingsService,
  ) {
  }

  ngOnInit() {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        if (params.changeDealOnSelectVehicle === "false") {
          this.changeDealOnSelectVehicle = false;
        }
        if (params.dispatchStockNumberOnly === "true") {
          this.dispatchStockNumberOnly = true;
        }
      });
    this.subToVehicles();
    this.subToSearchString();

    // Auto-refresh vehicle list when modal opens
    this.vehicleService.dispatchGetAllVehicles();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  isActiveSort(type: string): boolean {
    return this.sortParam === type;
  }

  private subToVehicles() {
    this.vehicleService.selectUnsoldVehicles()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((vehicles: VehicleWithCondition[]) => {
        setTimeout(() => {
          if ((this.vehicles || []).length === 0) {
            this.vehicleService.dispatchGetAllVehicles();
          }
        }, 5000);

        // blocked vehicles (never show these vehicles.  Issue #1393)
        const blockedVehicles = [26493, 26619, 28151, 28274, 28314, 28318, 28374, 28404, 28405, 28440, 28452, 28531];

        vehicles = vehicles.map(vehicle => {
          vehicle.condition = this.getCondition(vehicle);
          return vehicle;
        });

        this.vehicles = vehicles
          // filter out blocked stock numbers
          .filter(vehicle => !(blockedVehicles.includes(Number(vehicle.stockNumber))))
          // filter out vehicles that are not ready to be sold yet
          .filter(vehicle => !(vehicle.stockNumber.startsWith('P')));

        //console.log("vehicles", this.vehicles)
      });
  }

  private subToSearchString() {
    this.appService.selectSearchString()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((searchString: string) => {
        this.searchText = searchString;
        if (this.searchText) {
          this.filterVehicles();
          this.filter = true;
        } else {
          this.filter = false;
        }
      });
  }

  filterVehicles() {
    this.filteredVehicles = this.vehicles.filter((vehicle: Vehicle) => {
      return Object.keys(vehicle).filter(key => {
        const prop = vehicle[key];
        if (Array.isArray(prop)) {
          return prop.includes(this.searchText);
        } else {
          const stringProp = String(prop);
          return stringProp.toLowerCase().includes(this.searchText.toLowerCase());
        }
      }).length > 0;
    });
  }

  get vehiclesList() {
    return this.filter ? this.filteredVehicles : this.vehicles;
  }

  getCondition(vehicle: Vehicle) {
    const condition = this.vehicleService.vehicleCondition(vehicle);
    return condition ? condition.toUpperCase() : "";
  }

  numberWithCommas(x: any) {
    return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : 0;
  }

  truncateString(str: string, maxLength = 70): string {
    if (!str) return "";
    if (str.length <= maxLength) return str;
    return str.substr(0, maxLength);
  }

  // ACTIONS

  onSelectVehicle(vehicle: Vehicle) {
    // Show loading indicator
    swal.fire({
      title: 'Checking Vehicle Availability',
      html: '<div class="swal-spinner"></div><div style="margin-top: 10px;">Please wait...</div>',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showClass: {
        popup: '',
        backdrop: 'swal2-noanimation'
      },
      hideClass: {
        popup: ''
      }
    });

    // Check vehicle status before proceeding with selection
    this.vehicleService.checkVehicleStatus(vehicle.stockNumber).subscribe({
      next: (response) => {
        swal.close(); // Close loading indicator
        
        if (!response.available || response.status === 'CustomerFile') {
          this.vehicleService.displayVehicleUnavailableDialog(
            VehicleUnavailableError.NotAvailableForSale,
            vehicle.stockNumber,
            {popup: 'vehicle-unavailable-modal', confirmButton: 'vehicle-unavailable-ok-btn'}
          ).then(() => {
            // Refresh the list after user acknowledges
            this.vehicleService.dispatchGetAllVehicles();
          });
        } else {
          // Vehicle is available, proceed with original logic
          if (this.dispatchStockNumberOnly) {
            const navigationExtras: NavigationExtras = {queryParams: {stockNumber: vehicle.stockNumber}};
            this.router.navigate([{outlets: {modal: ["change-stock-number"]}}], navigationExtras).then();
          } else {
            this.vehicleService.dispatchSetVehicleByLookup(vehicle);
            this.dealService.updateVehicle(vehicle, this.changeDealOnSelectVehicle);

            // set all dealer accessories for the found vehicle to the deal by default
            const selectedDealerAccessories = [];
            if (vehicle?.customFields?.length > 0) {
              vehicle?.customFields.forEach(field => {
                if (field.key.startsWith("DealerAccDesc")) {
                  selectedDealerAccessories.push(field.value);
                }
              });
            }

            this.store.dispatch(dealActions.updateSelectedDealerAccessories({selectedDealerAccessories}));
            this.router.navigate([{outlets: {modal: null}}]).then();
          }
        }
      },
      error: (error) => {
        swal.close(); // Close loading indicator
        
        // If status check fails, log error but proceed anyway
        console.error('Error checking vehicle status:', error);

        // Proceed with original logic
        if (this.dispatchStockNumberOnly) {
          const navigationExtras: NavigationExtras = {queryParams: {stockNumber: vehicle.stockNumber}};
          this.router.navigate([{outlets: {modal: ["change-stock-number"]}}], navigationExtras).then();
        } else {
          this.vehicleService.dispatchSetVehicleByLookup(vehicle);
          this.dealService.updateVehicle(vehicle, this.changeDealOnSelectVehicle);

          // set all dealer accessories for the found vehicle to the deal by default
          const selectedDealerAccessories = [];
          if (vehicle?.customFields?.length > 0) {
            vehicle?.customFields.forEach(field => {
              if (field.key.startsWith("DealerAccDesc")) {
                selectedDealerAccessories.push(field.value);
              }
            });
          }

          this.store.dispatch(dealActions.updateSelectedDealerAccessories({selectedDealerAccessories}));
          this.router.navigate([{outlets: {modal: null}}]).then();
        }
      }
    });
  }

  onSelectSort(type: string) {
    this.sortParam = type;
    this.ascending = !this.ascending;
  }

  public getInventoryForVin() {
    this.settingsService.runPbsImporterForVin(this.getInventoryVin.trim()).pipe().subscribe({
      next: () => {
        this.vehicleService.dispatchGetAllVehicles();
        this.getSingleVehiclePanelOpen = false;
        this.appService.setSearch(this.getInventoryVin.trim());
        swal.fire({
          title: "Vehicle Import Complete",
          html: "The vehicle information for vin " + this.getInventoryVin + " has been retrieved successfully.",
          icon: "success",
          showConfirmButton: true,
          confirmButtonText: "OK",
        } as SweetAlertOptions).then(_ => {
        });
      },
      error: (e) => {
        if (e && e.indexOf("o vehicle found")) {
          swal.fire({
            title: "VIN Not Found",
            html: "The vin \"" + this.getInventoryVin + "\" could not be found. Check the vin you've entered and try again.",
            icon: "error",
            showConfirmButton: true,
            confirmButtonText: "OK",
          } as SweetAlertOptions).then();
        } else {
          swal.fire({
            title: "Inventory Import Error",
            html: "The request to import inventory has failed.  Please contact your System Administrator.",
            icon: "error",
            showConfirmButton: true,
            confirmButtonText: "OK",
          } as SweetAlertOptions).then();
        }
      }
    });
  }

  public getAllInventory() {
    this.settingsService.runPbsImporter().subscribe({
      next: () => {
        this.getAllVehiclesPanelOpen = false;
        swal.fire({
          title: "Inventory Import Started",
          html: "May take up to 1 hour.",
          icon: "success",
          showConfirmButton: true,
          confirmButtonText: "OK",
        } as SweetAlertOptions).then();
      },
      error: () => {
        swal.fire({
          title: "Inventory Import Error",
          html: "The request to import inventory has failed.  Please contact your System Administrator.",
          icon: "error",
          showConfirmButton: true,
          confirmButtonText: "OK",
        } as SweetAlertOptions).then();
      }
    });
  }

  public refreshVehicleList() {
    this.vehicleService.dispatchGetAllVehicles();
    swal.fire({
      title: "Vehicle List Refreshed",
      html: "List of vehicles has been refreshed.",
      icon: "success",
      showConfirmButton: true,
      confirmButtonText: "OK",
    } as SweetAlertOptions).then();
  }
}
